import type { ComponentProps } from 'react';
import React, { useState, useEffect } from 'react';

/* Services */
import {
  PlaceAutocompleteService,
  PlaceDetailsService,
} from 'services/GooglePlacesService';

/* Child Components */
import FocusContainer from 'components/signup/autocomplete/FocusContainer';
import PlaceAutocompleteList from 'components/signup/autocomplete/PlaceAutocompleteList';
import WishTextInput from 'components/common/WishTextInput';

/* Types */
import type {
  QueryablePlaceType,
  PlaceAutocompleteRequestType,
  PlaceAutocompletePredictionType,
  PlaceDetailsFieldType,
  PlaceDetailsRequestType,
  PlaceDetailsResultType,
} from 'types/GooglePlacesTypes';

/* Constants */
import { DEFAULT_LOCALE, DEFAULT_COUNTRY_CODE } from 'constants/AppConstants';

/* Utils */
import { delay } from 'utils/Promise';

// Rate limit the autocomplete requests to once in this period
const AUTOCOMPLETE_RATE_LIMIT_TIMEOUT_MS = 100;

type PropsType = ComponentProps<typeof WishTextInput> & {
  language?: string;
  country?: string;
  types?: Array<QueryablePlaceType>;
  sessionToken: string;
  resetSessionToken: () => void;
  detailsFields: Array<PlaceDetailsFieldType>;
  onDetails: (
    result: PlaceDetailsResultType,
    attributions: Array<string>,
  ) => void;
  onFocusChange?: (isFocused: boolean) => void;
  shouldShowList: boolean;
};

const PlaceAutocompleteTextInput = ({
  language,
  country,
  types,
  sessionToken,
  resetSessionToken,
  detailsFields,
  onDetails,
  onFocusChange,
  shouldShowList,
  value,
  onUpPressed: passedOnUpPressed,
  onDownPressed: passedOnDownPressed,
  onEnterPressed: passedOnEnterPressed,
  ...inputProps
}: PropsType) => {
  const [predictions, setPredictions] = useState<
    Array<PlaceAutocompletePredictionType>
  >([]);

  const [onTimeout, setOnTimeout] = useState<boolean>(false);
  const [queuedInput, setQueuedInput] = useState<string | null | undefined>(
    null,
  );

  const [focused, setFocused] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null | undefined>(
    null,
  );

  const changeFocus = (isFocused: boolean) => {
    setFocused(isFocused);
    if (onFocusChange) {
      onFocusChange(isFocused);
    }
  };

  const updateResults = async (input: string | null | undefined) => {
    if (!shouldShowList) {
      return;
    }
    if (onTimeout) {
      setQueuedInput(input);
      return;
    }
    if (!input || !focused) {
      setPredictions([]);
      return;
    }

    const request: PlaceAutocompleteRequestType = {
      sessiontoken: sessionToken,
      input,
      country: country?.toUpperCase() || DEFAULT_COUNTRY_CODE,
      language: language || DEFAULT_LOCALE,
    };
    if (types !== undefined && types.length > 0) {
      request.types = types;
    }

    setOnTimeout(true);
    delay(AUTOCOMPLETE_RATE_LIMIT_TIMEOUT_MS).then(() => setOnTimeout(false));

    const response = await PlaceAutocompleteService(request);
    setPredictions(response.predictions || []);
  };

  useEffect(() => {
    updateResults(value);
    changeFocus(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!onTimeout && queuedInput !== null) {
      updateResults(queuedInput);
      setQueuedInput(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onTimeout]);

  useEffect(() => {
    setSelectedIndex(null);
  }, [predictions]);

  const getDetails = async (placeId: string) => {
    const request: PlaceDetailsRequestType = {
      sessiontoken: sessionToken,
      fields: detailsFields,
      place_id: placeId,
    };
    if (language) {
      request.language = language;
    }
    resetSessionToken();

    const response = await PlaceDetailsService(request);
    setPredictions([]);
    if (response.result) {
      onDetails(response.result, response.html_attributions || []);
    }
  };

  const onSelectResult = (placeId: string) => {
    getDetails(placeId);
    changeFocus(false);
  };

  const onUpPressed = () => {
    if (selectedIndex === 0) {
      setSelectedIndex(null);
    } else if (selectedIndex != null) {
      setSelectedIndex(selectedIndex - 1);
    }
    if (passedOnUpPressed) {
      passedOnUpPressed();
    }
  };

  const onDownPressed = () => {
    const newSelectedIndex = selectedIndex == null ? 0 : selectedIndex + 1;
    if (newSelectedIndex < predictions.length) {
      setSelectedIndex(newSelectedIndex);
    }
    if (passedOnDownPressed) {
      passedOnDownPressed();
    }
  };

  const onEnterPressed = () => {
    if (selectedIndex != null && selectedIndex < predictions.length) {
      onSelectResult(predictions[selectedIndex].place_id);
    }
    if (passedOnEnterPressed) {
      passedOnEnterPressed();
    }
  };

  return (
    <FocusContainer onFocusChange={changeFocus}>
      <WishTextInput
        value={value}
        onUpPressed={onUpPressed}
        onDownPressed={onDownPressed}
        onEnterPressed={onEnterPressed}
        {...inputProps}
      />
      <PlaceAutocompleteList
        predictions={predictions}
        selectedIndex={selectedIndex}
        onOptionClicked={onSelectResult}
        visible={focused}
      />
    </FocusContainer>
  );
};

export default PlaceAutocompleteTextInput;
